// Arrays — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Arrays Explanation modal. Each version now carries concise steps AND an interactive
// "watch it happen" visualizer, same as the curated interview problems — so students see
// exactly what changes at each line, not just read a paragraph about it.

const arraysDrills = [
  {
    id: 'find-largest-element',
    title: 'Find the Largest Element in an Array',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Track the Running Maximum',
        explanation: 'Start assuming the first element is the largest, then walk the rest of the array once, replacing the tracked max any time a bigger value shows up.',
        steps: [
          'Assume arr[0] is the largest so far — set max to it.',
          'Compare every remaining element against the current max.',
          'Whenever a bigger value appears, replace max with it.',
          'After the last element, max holds the true largest value.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [3, 7, 2, 9, 4] }],
          varNames: ['max'],
          steps: [
            { highlights: { arr: [0] }, vars: { max: 3 }, note: 'Start: max = arr[0] = 3.' },
            { highlights: { arr: [1] }, vars: { max: 7 }, note: '7 > 3 → new max: 7.' },
            { highlights: { arr: [2] }, vars: { max: 7 }, note: '2 is not > 7 → max stays 7.' },
            { highlights: { arr: [3] }, vars: { max: 9 }, note: '9 > 7 → new max: 9.' },
            { highlights: { arr: [4] }, vars: { max: 9 }, note: '4 is not > 9 → max stays 9. Final answer: 9.' },
          ],
        },
        code:
`public int findLargest(int[] arr) {
    int max = arr[0];
    for (int i = 1; i < arr.length; i++) {
        if (arr[i] > max) {
            max = arr[i];
        }
    }
    return max;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized / Idiomatic — Java Streams',
        explanation: 'Same O(n) scan under the hood, but expressed declaratively. Good to know exists, though most interviewers want to see you derive the manual loop version first.',
        steps: [
          'Arrays.stream(arr) turns the array into a stream of ints.',
          '.max() scans it once and returns the largest value, wrapped in an OptionalInt.',
          '.getAsInt() unwraps that Optional into a plain int.',
          'Same single O(n) scan as the basic loop — just written declaratively, with no visible loop.',
        ],
        code:
`public int findLargestStream(int[] arr) {
    return Arrays.stream(arr).max().getAsInt();
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-sum-average',
    title: 'Find the Sum and Average of Array Elements',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Accumulate in One Pass',
        explanation: 'Add every element into a running sum, then divide by the count. Casting to double before dividing matters — int division would silently truncate the average.',
        steps: [
          'Start sum at 0.',
          'Add every element into sum, one at a time.',
          'Cast sum to double before dividing — int ÷ int truncates in Java.',
          'Divide by arr.length to get the average.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [4, 8, 2, 6] }],
          varNames: ['sum'],
          steps: [
            { highlights: { arr: [0] }, vars: { sum: 4 }, note: 'sum = 0 + 4 = 4.' },
            { highlights: { arr: [1] }, vars: { sum: 12 }, note: 'sum = 4 + 8 = 12.' },
            { highlights: { arr: [2] }, vars: { sum: 14 }, note: 'sum = 12 + 2 = 14.' },
            { highlights: { arr: [3] }, vars: { sum: 20 }, note: 'sum = 14 + 6 = 20. Average = (double) 20 / 4 = 5.0.' },
          ],
        },
        code:
`public double findAverage(int[] arr) {
    int sum = 0;
    for (int num : arr) {
        sum += num;
    }
    return (double) sum / arr.length;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized / Idiomatic — Java Streams',
        explanation: 'IntStream.average() already returns a proper double, sidestepping the int-division trap entirely.',
        steps: [
          'Arrays.stream(arr) builds an IntStream from the array.',
          '.average() internally sums and divides, returning an OptionalDouble.',
          '.getAsDouble() unwraps the result.',
          'No manual cast needed — average() is already double-safe by design.',
        ],
        code:
`public double findAverageStream(int[] arr) {
    return Arrays.stream(arr).average().getAsDouble();
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'check-array-sorted',
    title: 'Check Whether an Array Is Sorted',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Compare Every Pair',
        explanation: 'Technically correct: check every (i, j) pair for an inversion. But sortedness only ever depends on neighboring elements, so this does far more work than it needs to.',
        steps: [
          'Assume sorted = true at the start.',
          'Compare every pair (i, j) with j > i.',
          'If an earlier element is bigger than a later one, set sorted = false.',
          'Keep checking every remaining pair even after finding a violation — no early exit.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [1, 5, 3, 8] }],
          varNames: ['sorted'],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [1] } }, vars: { sorted: 'true' }, note: 'Compare arr[0]=1 and arr[1]=5: 1 is not > 5 — no violation.' },
            { highlights: { arr: { primary: [0], secondary: [2] } }, vars: { sorted: 'true' }, note: 'Compare arr[0]=1 and arr[2]=3: no violation.' },
            { highlights: { arr: { primary: [0], secondary: [3] } }, vars: { sorted: 'true' }, note: 'Compare arr[0]=1 and arr[3]=8: no violation.' },
            { highlights: { arr: { primary: [1], secondary: [2] } }, vars: { sorted: 'false' }, note: 'Compare arr[1]=5 and arr[2]=3: 5 > 3 → violation found! sorted = false.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, vars: { sorted: 'false' }, note: 'Compare arr[1]=5 and arr[3]=8: no violation — but sorted is already false and we keep going anyway.' },
            { highlights: { arr: { primary: [2], secondary: [3] } }, vars: { sorted: 'false' }, note: 'Compare arr[2]=3 and arr[3]=8: no violation. All 6 pairs checked for a 4-element array — final answer: false.' },
          ],
        },
        code:
`public boolean isSortedBruteForce(int[] arr) {
    boolean sorted = true;
    for (int i = 0; i < arr.length; i++) {
        for (int j = i + 1; j < arr.length; j++) {
            if (arr[i] > arr[j]) sorted = false;
        }
    }
    return sorted;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Adjacent Comparison with Early Exit',
        explanation: 'If every adjacent pair is non-decreasing, the whole array is non-decreasing — non-adjacent pairs never need checking. Also exits the instant the first violation is found.',
        steps: [
          'Compare each element only to its immediate previous neighbor.',
          'If arr[i] < arr[i-1], the array is not sorted — return false right away.',
          'If the loop finishes with no violation, the array is sorted.',
          'Exits the instant a problem is found, instead of checking every remaining pair.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [1, 5, 3, 8] }],
          varNames: [],
          steps: [
            { highlights: { arr: [1] }, note: 'Compare arr[1]=5 to arr[0]=1: 5 is not less than 1 — continue.' },
            { highlights: { arr: [2] }, note: 'Compare arr[2]=3 to arr[1]=5: 3 < 5 → violation! Return false immediately — index 3 is never even checked.' },
          ],
        },
        code:
`public boolean isSorted(int[] arr) {
    for (int i = 1; i < arr.length; i++) {
        if (arr[i] < arr[i - 1]) {
            return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'reverse-array-in-place',
    title: 'Reverse an Array In Place',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Build a New Reversed Array',
        explanation: 'Place each element at its mirrored position in a brand-new array. Correct, but costs O(n) extra memory for something that can be done without any extra array.',
        steps: [
          'Create a new array the same size as the original.',
          'For each index i, place arr[i] into the mirrored position reversed[n-1-i].',
          'The original array stays untouched — reversed is a separate copy.',
          'Costs O(n) extra memory for that second array.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [10, 20, 30, 40, 50] },
            { label: 'reversed', values: [null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { arr: [0], reversed: { secondary: [4] } }, snapshots: { reversed: [null, null, null, null, 10] }, note: 'arr[0]=10 → reversed[4] = 10.' },
            { highlights: { arr: [1], reversed: { secondary: [3] } }, snapshots: { reversed: [null, null, null, 20, 10] }, note: 'arr[1]=20 → reversed[3] = 20.' },
            { highlights: { arr: [2], reversed: { secondary: [2] } }, snapshots: { reversed: [null, null, 30, 20, 10] }, note: 'arr[2]=30 → reversed[2] = 30.' },
            { highlights: { arr: [3], reversed: { secondary: [1] } }, snapshots: { reversed: [null, 40, 30, 20, 10] }, note: 'arr[3]=40 → reversed[1] = 40.' },
            { highlights: { arr: [4], reversed: { secondary: [0] } }, snapshots: { reversed: [50, 40, 30, 20, 10] }, note: 'arr[4]=50 → reversed[0] = 50. Done — but a full second array was needed.' },
          ],
        },
        code:
`public int[] reverseWithExtraArray(int[] arr) {
    int n = arr.length;
    int[] reversed = new int[n];
    for (int i = 0; i < n; i++) {
        reversed[n - 1 - i] = arr[i];
    }
    return reversed;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Two-Pointer In-Place Swap',
        explanation: 'Swap from both ends moving inward until the pointers meet. No second array needed at all.',
        steps: [
          'Set left = 0 and right = the last index.',
          'Swap arr[left] and arr[right].',
          'Move left forward by one and right backward by one.',
          'Stop the moment left meets or passes right.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [10, 20, 30, 40, 50] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [4] } }, snapshots: { arr: [50, 20, 30, 40, 10] }, note: 'Swap arr[0] and arr[4]: 10 ↔ 50.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, snapshots: { arr: [50, 40, 30, 20, 10] }, note: 'Swap arr[1] and arr[3]: 20 ↔ 40.' },
            { highlights: { arr: { primary: [2] } }, snapshots: { arr: [50, 40, 30, 20, 10] }, note: 'left = right = 2 — pointers meet in the middle. Fully reversed, no second array used.' },
          ],
        },
        code:
`public void reverseInPlace(int[] arr) {
    int left = 0, right = arr.length - 1;
    while (left < right) {
        int temp = arr[left];
        arr[left] = arr[right];
        arr[right] = temp;
        left++;
        right--;
    }
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'count-occurrences',
    title: 'Count How Many Times a Value Appears',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Linear Tally',
        explanation: 'Walk the array once, incrementing a counter every time the target is seen. For an unsorted array this is already optimal — every element must be looked at at least once.',
        steps: [
          'Start count at 0.',
          'Walk through every element once.',
          'Whenever an element equals the target, increment count.',
          'After the loop, count holds the total number of occurrences.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 2)', values: [2, 5, 2, 8, 2, 3] }],
          varNames: ['count'],
          steps: [
            { highlights: { 'arr (target = 2)': [0] }, vars: { count: 1 }, note: 'arr[0]=2 matches target → count = 1.' },
            { highlights: { 'arr (target = 2)': [1] }, vars: { count: 1 }, note: 'arr[1]=5 does not match → count stays 1.' },
            { highlights: { 'arr (target = 2)': [2] }, vars: { count: 2 }, note: 'arr[2]=2 matches → count = 2.' },
            { highlights: { 'arr (target = 2)': [3] }, vars: { count: 2 }, note: 'arr[3]=8 does not match → count stays 2.' },
            { highlights: { 'arr (target = 2)': [4] }, vars: { count: 3 }, note: 'arr[4]=2 matches → count = 3.' },
            { highlights: { 'arr (target = 2)': [5] }, vars: { count: 3 }, note: 'arr[5]=3 does not match → count stays 3. Final answer: 3.' },
          ],
        },
        code:
`public int countOccurrences(int[] arr, int target) {
    int count = 0;
    for (int num : arr) {
        if (num == target) count++;
    }
    return count;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Only Faster If the Array Is Already Sorted',
        explanation: 'binarySearch jumps to any one occurrence in O(log n), then expands outward to count matching neighbors. This wins over the linear scan when duplicates are sparse — with many duplicates it degrades back toward O(n), so it\'s a conditional improvement, not a guaranteed one.',
        steps: [
          'The array must already be sorted for this to work at all.',
          'binarySearch jumps directly to any one occurrence of the target.',
          'Expand left and right from that index while neighbors keep matching.',
          'Total count is 1 (the found index) plus every matching neighbor on both sides.',
        ],
        visual: {
          arrays: [{ label: 'sortedArr (target = 2)', values: [2, 2, 2, 3, 5, 8] }],
          varNames: ['count'],
          steps: [
            { highlights: { 'sortedArr (target = 2)': [1] }, vars: { count: 1 }, note: 'binarySearch lands directly on index 1 (value 2) — one match found immediately.' },
            { highlights: { 'sortedArr (target = 2)': [0] }, vars: { count: 2 }, note: 'Expand left: sortedArr[0]=2 also matches → count = 2.' },
            { highlights: { 'sortedArr (target = 2)': [2] }, vars: { count: 3 }, note: 'Expand right: sortedArr[2]=2 also matches → count = 3.' },
            { highlights: { 'sortedArr (target = 2)': [3] }, vars: { count: 3 }, note: 'sortedArr[3]=3 does not match → stop expanding right. Final answer: 3.' },
          ],
        },
        code:
`public int countOccurrencesSorted(int[] arr, int target) {
    int idx = Arrays.binarySearch(arr, target);
    if (idx < 0) return 0;

    int count = 1, left = idx - 1, right = idx + 1;
    while (left >= 0 && arr[left] == target) { count++; left--; }
    while (right < arr.length && arr[right] == target) { count++; right++; }
    return count;
}`,
        complexity: { time: 'O(log n) best case, O(n) worst case', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'second-largest-no-sort',
    title: 'Find the Second-Largest Element Without Sorting',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort, Then Read the Second-to-Last',
        explanation: 'Easiest to write, but sorting the whole array just to answer a question that only needs the top two values is wasteful. A defensive copy is made first so the caller\'s original order survives.',
        steps: [
          'Copy the array so the caller\'s original order is never disturbed.',
          'Sort the copy in ascending order.',
          'The largest value ends up at the very last index.',
          'The second-largest is the element right before it.',
        ],
        visual: {
          arrays: [{ label: 'copy', values: [3, 8, 1, 9, 5] }],
          varNames: [],
          steps: [
            { highlights: { copy: [] }, note: 'Original copy, unsorted: [3, 8, 1, 9, 5].' },
            { highlights: { copy: [3, 4] }, snapshots: { copy: [1, 3, 5, 8, 9] }, note: 'After Arrays.sort(copy): [1, 3, 5, 8, 9]. Second-to-last (index 3) = 8 — the answer.' },
          ],
        },
        code:
`public int secondLargestSorting(int[] arr) {
    int[] copy = Arrays.copyOf(arr, arr.length);
    Arrays.sort(copy);
    return copy[copy.length - 2];
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Single Pass, Track Top Two',
        explanation: 'Track the largest and second-largest values seen so far in one sweep. When a new largest appears, the OLD largest demotes into second place instead of being thrown away.',
        steps: [
          'Start largest and secondLargest at the smallest possible value.',
          'If the current element beats largest, the old largest demotes to secondLargest first.',
          'Otherwise, if it beats secondLargest (and isn\'t equal to largest), update secondLargest directly.',
          'One pass — no sorting required at all.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [3, 8, 1, 9, 5] }],
          varNames: ['largest', 'secondLargest'],
          steps: [
            { highlights: { arr: [0] }, vars: { largest: 3, secondLargest: '-∞' }, note: '3 > -∞ → largest = 3.' },
            { highlights: { arr: [1] }, vars: { largest: 8, secondLargest: 3 }, note: '8 > 3 → old largest (3) demotes to secondLargest. largest becomes 8.' },
            { highlights: { arr: [2] }, vars: { largest: 8, secondLargest: 3 }, note: '1 beats neither 8 nor 3 → nothing changes.' },
            { highlights: { arr: [3] }, vars: { largest: 9, secondLargest: 8 }, note: '9 > 8 → old largest (8) demotes to secondLargest. largest becomes 9.' },
            { highlights: { arr: [4] }, vars: { largest: 9, secondLargest: 8 }, note: '5 beats neither 9 nor 8 → nothing changes. Final: largest = 9, secondLargest = 8.' },
          ],
        },
        code:
`public int secondLargest(int[] arr) {
    int largest = Integer.MIN_VALUE, secondLargest = Integer.MIN_VALUE;
    for (int num : arr) {
        if (num > largest) {
            secondLargest = largest;
            largest = num;
        } else if (num > secondLargest && num != largest) {
            secondLargest = num;
        }
    }
    return secondLargest;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'rotate-array-left',
    title: 'Rotate an Array Left by k Positions',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Build a New Rotated Array',
        explanation: 'Each output position is computed directly with modular arithmetic. Correct and O(n), but needs a second array; k % n keeps it safe even if k is larger than the array itself.',
        steps: [
          'Reduce k modulo n in case k is larger than the array.',
          'For each output index i, pull the value from arr[(i + k) % n].',
          'The modulo wraps the index back to the front once it passes the end.',
          'Fill a brand-new array this way — the original stays untouched.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [1, 2, 3, 4, 5] },
            { label: 'rotated', values: [null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { rotated: { secondary: [0] } }, snapshots: { rotated: [3, null, null, null, null] }, note: 'rotated[0] = arr[(0+2)%5] = arr[2] = 3.' },
            { highlights: { rotated: { secondary: [1] } }, snapshots: { rotated: [3, 4, null, null, null] }, note: 'rotated[1] = arr[(1+2)%5] = arr[3] = 4.' },
            { highlights: { rotated: { secondary: [2] } }, snapshots: { rotated: [3, 4, 5, null, null] }, note: 'rotated[2] = arr[(2+2)%5] = arr[4] = 5.' },
            { highlights: { rotated: { secondary: [3] } }, snapshots: { rotated: [3, 4, 5, 1, null] }, note: 'rotated[3] = arr[(3+2)%5] = arr[0] = 1 — the index wrapped around to the front.' },
            { highlights: { rotated: { secondary: [4] } }, snapshots: { rotated: [3, 4, 5, 1, 2] }, note: 'rotated[4] = arr[(4+2)%5] = arr[1] = 2. Final: [3, 4, 5, 1, 2].' },
          ],
        },
        code:
`public int[] rotateLeftExtraArray(int[] arr, int k) {
    int n = arr.length;
    k = k % n;
    int[] rotated = new int[n];
    for (int i = 0; i < n; i++) {
        rotated[i] = arr[(i + k) % n];
    }
    return rotated;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — In-Place via Triple Reversal',
        explanation: 'Reverse the first k elements, then the rest, then the whole array. Three small reversals rearrange everything correctly with no second array — the classic "juggling" rotation trick.',
        steps: [
          'Reverse just the first k elements.',
          'Reverse the remaining n − k elements.',
          'Reverse the entire array.',
          'Three small in-place reversals — no second array needed.',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 2)', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { 'arr (k = 2)': { primary: [0, 1] } }, snapshots: { 'arr (k = 2)': [2, 1, 3, 4, 5] }, note: 'Reverse the first k = 2 elements: [1, 2] → [2, 1].' },
            { highlights: { 'arr (k = 2)': { primary: [2, 3, 4] } }, snapshots: { 'arr (k = 2)': [2, 1, 5, 4, 3] }, note: 'Reverse the remaining elements: [3, 4, 5] → [5, 4, 3].' },
            { highlights: { 'arr (k = 2)': { primary: [0, 1, 2, 3, 4] } }, snapshots: { 'arr (k = 2)': [3, 4, 5, 1, 2] }, note: 'Reverse the whole array: [2, 1, 5, 4, 3] → [3, 4, 5, 1, 2]. Rotation complete.' },
          ],
        },
        code:
`public void rotateLeftInPlace(int[] arr, int k) {
    int n = arr.length;
    k = k % n;
    reverse(arr, 0, k - 1);
    reverse(arr, k, n - 1);
    reverse(arr, 0, n - 1);
}

private void reverse(int[] arr, int start, int end) {
    while (start < end) {
        int temp = arr[start];
        arr[start] = arr[end];
        arr[end] = temp;
        start++;
        end--;
    }
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'move-zeros-to-end',
    title: 'Move All Zeros to the End',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Build a New Array',
        explanation: 'Copy every non-zero element into a new array first; the leftover slots are already 0 by Java\'s default. Correct, but uses O(n) extra memory.',
        steps: [
          'Create a new array the same size as the original.',
          'Walk the original array once, copying only non-zero values in order.',
          'Java already fills the remaining slots with 0 by default.',
          'The original array\'s non-zero order is preserved.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [0, 1, 0, 3, 12] },
            { label: 'result', values: [null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { arr: [1], result: { secondary: [0] } }, snapshots: { result: [1, null, null, null, null] }, note: 'arr[1]=1 is non-zero → result[0] = 1.' },
            { highlights: { arr: [3], result: { secondary: [1] } }, snapshots: { result: [1, 3, null, null, null] }, note: 'arr[3]=3 is non-zero → result[1] = 3.' },
            { highlights: { arr: [4], result: { secondary: [2] } }, snapshots: { result: [1, 3, 12, null, null] }, note: 'arr[4]=12 is non-zero → result[2] = 12.' },
            { highlights: { result: [3, 4] }, snapshots: { result: [1, 3, 12, 0, 0] }, note: 'Remaining slots stay 0 by default. Final: [1, 3, 12, 0, 0].' },
          ],
        },
        code:
`public int[] moveZerosExtraArray(int[] arr) {
    int[] result = new int[arr.length];
    int idx = 0;
    for (int num : arr) {
        if (num != 0) result[idx++] = num;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Two-Pointer In-Place',
        explanation: 'insertPos tracks where the next non-zero value should land. After one pass places every non-zero at the front in order, the remaining tail is simply overwritten with zeros.',
        steps: [
          'insertPos starts at 0 — where the next non-zero value should land.',
          'Scan the array; whenever a non-zero value is found, write it to insertPos and advance insertPos.',
          'Zeros are simply skipped as the scan passes over them.',
          'Once the scan finishes, fill everything from insertPos to the end with 0.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [0, 1, 0, 3, 12] }],
          varNames: ['insertPos'],
          steps: [
            { highlights: { arr: [1] }, snapshots: { arr: [1, 1, 0, 3, 12] }, vars: { insertPos: 1 }, note: 'arr[1]=1 is non-zero → write it to index 0. insertPos becomes 1.' },
            { highlights: { arr: [2] }, snapshots: { arr: [1, 1, 0, 3, 12] }, vars: { insertPos: 1 }, note: 'arr[2]=0 → skipped entirely. insertPos stays 1.' },
            { highlights: { arr: [3] }, snapshots: { arr: [1, 3, 0, 3, 12] }, vars: { insertPos: 2 }, note: 'arr[3]=3 is non-zero → write it to index 1. insertPos becomes 2.' },
            { highlights: { arr: [4] }, snapshots: { arr: [1, 3, 12, 3, 12] }, vars: { insertPos: 3 }, note: 'arr[4]=12 is non-zero → write it to index 2. insertPos becomes 3.' },
            { highlights: { arr: [3, 4] }, snapshots: { arr: [1, 3, 12, 0, 0] }, vars: { insertPos: 5 }, note: 'Fill everything from index 3 onward with 0. Final: [1, 3, 12, 0, 0] — same array, no second array used.' },
          ],
        },
        code:
`public void moveZerosInPlace(int[] arr) {
    int insertPos = 0;
    for (int num : arr) {
        if (num != 0) {
            arr[insertPos++] = num;
        }
    }
    while (insertPos < arr.length) {
        arr[insertPos++] = 0;
    }
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-missing-number',
    title: 'Find the Missing Number in a Sequence of 1 to n',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — HashSet Lookup',
        explanation: 'Mark every number that IS present, then scan 1..n for the first one missing from the set. Correct, but pays O(n) extra memory for the set.',
        steps: [
          'Add every number from arr into a HashSet.',
          'Loop from 1 to n in order.',
          'The first number not found in the set is the missing one.',
          'Costs O(n) extra memory for the set itself.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [1, 2, 4, 5] },
            { label: 'checking 1..n', values: [1, 2, 3, 4, 5] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'checking 1..n': [0] }, note: 'Is 1 in the set built from arr? Yes — continue.' },
            { highlights: { 'checking 1..n': [1] }, note: 'Is 2 in the set? Yes — continue.' },
            { highlights: { 'checking 1..n': [2] }, note: 'Is 3 in the set? No — this is the missing number. Stop here.' },
          ],
        },
        code:
`public int findMissingHashSet(int[] arr, int n) {
    Set<Integer> seen = new HashSet<>();
    for (int num : arr) seen.add(num);

    for (int i = 1; i <= n; i++) {
        if (!seen.contains(i)) return i;
    }
    return -1;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Sum Formula',
        explanation: 'The sum of 1..n has a closed-form formula, n(n+1)/2. Subtracting the array\'s actual sum from that expected total directly reveals the missing number — no extra data structure at all.',
        steps: [
          'Compute expectedSum using n(n+1)/2, for n=5 that\'s 15.',
          'Walk arr once, accumulating actualSum.',
          'Subtract: expectedSum − actualSum is exactly the missing number.',
          'No set, no extra memory — just two sums.',
        ],
        visual: {
          arrays: [{ label: 'arr (n = 5)', values: [1, 2, 4, 5] }],
          varNames: ['actualSum'],
          steps: [
            { highlights: { 'arr (n = 5)': [0] }, vars: { actualSum: 1 }, note: 'actualSum = 0 + 1 = 1.' },
            { highlights: { 'arr (n = 5)': [1] }, vars: { actualSum: 3 }, note: 'actualSum = 1 + 2 = 3.' },
            { highlights: { 'arr (n = 5)': [2] }, vars: { actualSum: 7 }, note: 'actualSum = 3 + 4 = 7.' },
            { highlights: { 'arr (n = 5)': [3] }, vars: { actualSum: 12 }, note: 'actualSum = 7 + 5 = 12. expectedSum (1..5) = 15. Missing = 15 − 12 = 3.' },
          ],
        },
        code:
`public int findMissingSumFormula(int[] arr, int n) {
    int expectedSum = n * (n + 1) / 2;
    int actualSum = 0;
    for (int num : arr) actualSum += num;
    return expectedSum - actualSum;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'kadane-max-subarray-drill',
    title: "Maximum Sum of a Contiguous Subarray (Kadane's Algorithm)",
    level: 'Advanced',
    crossReference:
      "This exact problem gets the full treatment — brute force, Kadane's optimized approach, step-by-step reasoning, and an interactive visualizer — in the curated \"Maximum Subarray\" problem in the Arrays problem list above. Open that one for the complete walkthrough instead of a duplicate here.",
  },
  {
    id: 'find-leaders',
    title: 'Find the Leaders in an Array',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Compare Each Element to Everything on Its Right',
        explanation: 'For every element, scan everything to its right looking for something bigger. Correct, but repeats overlapping work for every starting position.',
        steps: [
          'For each element, scan every element to its right.',
          'If nothing on the right is bigger, the element is a leader.',
          'If something bigger IS found, stop scanning and move to the next element.',
          'Every starting position triggers its own scan — O(n²) overall.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [16, 17, 4, 3, 5, 2] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [1] } }, note: 'Check 16: is 17 (to its right) bigger? Yes → 16 is NOT a leader.' },
            { highlights: { arr: { primary: [1], secondary: [2, 3, 4, 5] } }, note: 'Check 17: nothing to its right (4, 3, 5, 2) beats it → 17 IS a leader.' },
            { highlights: { arr: { primary: [2], secondary: [4] } }, note: 'Check 4: 5 (to its right) is bigger → 4 is NOT a leader.' },
            { highlights: { arr: { primary: [3], secondary: [4] } }, note: 'Check 3: 5 (to its right) is bigger → 3 is NOT a leader.' },
            { highlights: { arr: { primary: [4], secondary: [5] } }, note: 'Check 5: nothing to its right beats it → 5 IS a leader.' },
            { highlights: { arr: { primary: [5] } }, note: 'Check 2 (the last element): always a leader by default. Leaders found, left to right: 17, 5, 2.' },
          ],
        },
        code:
`public List<Integer> findLeadersBruteForce(int[] arr) {
    List<Integer> leaders = new ArrayList<>();
    for (int i = 0; i < arr.length; i++) {
        boolean isLeader = true;
        for (int j = i + 1; j < arr.length; j++) {
            if (arr[j] > arr[i]) {
                isLeader = false;
                break;
            }
        }
        if (isLeader) leaders.add(arr[i]);
    }
    return leaders;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Right-to-Left with a Running Maximum',
        explanation: 'Scan from the right, keeping a running maximum of everything already seen. An element is a leader exactly when it beats every max seen so far — checked in one backward pass, then the collected list is reversed to restore left-to-right order.',
        steps: [
          'Start from the rightmost element — it\'s always a leader.',
          'Track maxFromRight as the running best while moving leftward.',
          'Any element bigger than maxFromRight is a new leader; update maxFromRight when that happens.',
          'Reverse the collected list at the end to restore left-to-right order.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [16, 17, 4, 3, 5, 2] }],
          varNames: ['maxFromRight'],
          steps: [
            { highlights: { arr: [5] }, vars: { maxFromRight: 2 }, note: 'Start at the last element: 2 is always a leader. maxFromRight = 2.' },
            { highlights: { arr: [4] }, vars: { maxFromRight: 5 }, note: '5 > 2 → new leader. maxFromRight becomes 5.' },
            { highlights: { arr: [3] }, vars: { maxFromRight: 5 }, note: '3 is not > 5 → not a leader.' },
            { highlights: { arr: [2] }, vars: { maxFromRight: 5 }, note: '4 is not > 5 → not a leader.' },
            { highlights: { arr: [1] }, vars: { maxFromRight: 17 }, note: '17 > 5 → new leader. maxFromRight becomes 17.' },
            { highlights: { arr: [0] }, vars: { maxFromRight: 17 }, note: '16 is not > 17 → not a leader. One backward pass instead of nested scans. Leaders (reversed to left-to-right): 17, 5, 2.' },
          ],
        },
        code:
`public List<Integer> findLeaders(int[] arr) {
    List<Integer> leaders = new ArrayList<>();
    int maxFromRight = arr[arr.length - 1];
    leaders.add(maxFromRight); // the last element is always a leader

    for (int i = arr.length - 2; i >= 0; i--) {
        if (arr[i] > maxFromRight) {
            maxFromRight = arr[i];
            leaders.add(arr[i]);
        }
    }
    Collections.reverse(leaders);
    return leaders;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra (excluding output)' },
      },
    ],
  },
  {
    id: 'alternate-positive-negative',
    title: 'Rearrange Positive and Negative Numbers Alternately',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Split Into Two Lists, Then Weave',
        explanation: 'Separate positives and negatives into their own lists first, then interleave them one from each side at a time. Clear and correct, but needs two extra lists.',
        steps: [
          'Walk the array once, sorting each number into a positives list or a negatives list.',
          'Weave the two lists back together, alternating one element from each.',
          'Once one list runs out, append whatever remains from the other.',
          'Needs two extra lists worth of memory.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [1, 2, -3, -1, 4, -5] },
            { label: 'result', values: [null, null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { result: { secondary: [0] } }, snapshots: { result: [1, null, null, null, null, null] }, note: 'Take the first positive (1) → result[0] = 1.' },
            { highlights: { result: { secondary: [1] } }, snapshots: { result: [1, -3, null, null, null, null] }, note: 'Take the first negative (-3) → result[1] = -3.' },
            { highlights: { result: { secondary: [2] } }, snapshots: { result: [1, -3, 2, null, null, null] }, note: 'Take the next positive (2) → result[2] = 2.' },
            { highlights: { result: { secondary: [3] } }, snapshots: { result: [1, -3, 2, -1, null, null] }, note: 'Take the next negative (-1) → result[3] = -1.' },
            { highlights: { result: { secondary: [4] } }, snapshots: { result: [1, -3, 2, -1, 4, null] }, note: 'Take the next positive (4) → result[4] = 4.' },
            { highlights: { result: { secondary: [5] } }, snapshots: { result: [1, -3, 2, -1, 4, -5] }, note: 'Take the last negative (-5) → result[5] = -5. Both lists ran out at the same time here.' },
          ],
        },
        code:
`public int[] rearrangeAlternateExtraArray(int[] arr) {
    List<Integer> positives = new ArrayList<>();
    List<Integer> negatives = new ArrayList<>();
    for (int num : arr) {
        if (num >= 0) positives.add(num);
        else negatives.add(num);
    }

    int[] result = new int[arr.length];
    int i = 0, p = 0, n = 0;
    while (p < positives.size() && n < negatives.size()) {
        result[i++] = positives.get(p++);
        result[i++] = negatives.get(n++);
    }
    while (p < positives.size()) result[i++] = positives.get(p++);
    while (n < negatives.size()) result[i++] = negatives.get(n++);
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Single Pass, Direct Alternating Fill',
        explanation: 'Instead of building two separate lists and merging them afterward, write positives directly into even output slots and negatives into odd output slots as you scan once. Leaner on memory than the two-list version, though still O(n) extra overall — a true O(1)-space in-place version exists but needs a more intricate rotation trick that\'s rarely worth it for this problem.',
        steps: [
          'Set posIdx = 0 (even output slots) and negIdx = 1 (odd output slots).',
          'Scan the array once: write each positive into result[posIdx], then advance posIdx by 2.',
          'Write each negative into result[negIdx], then advance negIdx by 2.',
          'One pass, one output array — no intermediate lists at all.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [1, 2, -3, -1, 4, -5] },
            { label: 'result', values: [null, null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { arr: [0], result: { secondary: [0] } }, snapshots: { result: [1, null, null, null, null, null] }, note: 'arr[0]=1 is positive → result[0] = 1. posIdx becomes 2.' },
            { highlights: { arr: [1], result: { secondary: [2] } }, snapshots: { result: [1, null, 2, null, null, null] }, note: 'arr[1]=2 is positive → result[2] = 2. posIdx becomes 4.' },
            { highlights: { arr: [2], result: { secondary: [1] } }, snapshots: { result: [1, -3, 2, null, null, null] }, note: 'arr[2]=-3 is negative → result[1] = -3. negIdx becomes 3.' },
            { highlights: { arr: [3], result: { secondary: [3] } }, snapshots: { result: [1, -3, 2, -1, null, null] }, note: 'arr[3]=-1 is negative → result[3] = -1. negIdx becomes 5.' },
            { highlights: { arr: [4], result: { secondary: [4] } }, snapshots: { result: [1, -3, 2, -1, 4, null] }, note: 'arr[4]=4 is positive → result[4] = 4. posIdx becomes 6.' },
            { highlights: { arr: [5], result: { secondary: [5] } }, snapshots: { result: [1, -3, 2, -1, 4, -5] }, note: 'arr[5]=-5 is negative → result[5] = -5. Final: [1, -3, 2, -1, 4, -5].' },
          ],
        },
        code:
`public int[] rearrangeAlternate(int[] arr) {
    int[] result = new int[arr.length];
    int posIdx = 0, negIdx = 1;
    for (int num : arr) {
        if (num >= 0 && posIdx < arr.length) {
            result[posIdx] = num;
            posIdx += 2;
        } else if (num < 0 && negIdx < arr.length) {
            result[negIdx] = num;
            negIdx += 2;
        }
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
];

export default arraysDrills;
